import { useMemo } from 'react'
import { usePageParams } from '../../utils/usePageParams'
import { FilterType, type FilterProps } from '../Filter'
import { mergeFilter, type FilterBy, type FilterTemplate } from './FilterTemplate'
import { useUrlFilters } from './useUrlFilters'
import { parseDateFilterValue } from './dateFilter'

export type ListFilterField = FilterProps & {
    operator?: 'is' | 'matches'
    formatValue?: (value: string) => string
    /** Legacy keys, in precedence order, read before the canonical paramName. */
    aliases?: string[]
}

export interface ListFilterOptions {
    fields: ListFilterField[]
    rawTextParam?: string
    pageParamName?: string
}

function fieldTemplate(field: ListFilterField): FilterTemplate {
    const template: FilterTemplate = {
        key: field.label,
        operator: field.operator ?? (field.type === FilterType.text ? 'matches' : 'is'),
    }
    if (field.type === FilterType.checkbox) {
        template.possibleValuesSync = () => [
            { value: 'true', displayName: 'Yes' },
            { value: 'false', displayName: 'No' },
        ]
    } else if (field.type === FilterType.select) {
        template.possibleValuesSync = () =>
            (field.options ?? []).map(({ name, value }) => ({ value, displayName: name }))
    } else if (field.type === FilterType.autocomplete) {
        template.possibleValues = async (text) =>
            (await field.loadOptions(text || undefined)).map(({ name, value }) => ({ value, displayName: name }))
    } else if (field.type === FilterType.datetime) {
        template.placeholder = `YYYY-MM-DD HH:MM:SS${field.timezone === 'UTC' ? ' (UTC)' : ''}`
        template.parseValue = (value) => parseDateFilterValue(value, field)
    }
    return template
}

function toFilter(field: ListFilterField, value: string): FilterBy | undefined {
    if (!value) return undefined
    if (field.type === FilterType.checkbox && value === String(field.defaultValue ?? false)) return undefined
    let displayName = field.formatValue?.(value) ?? value
    if (field.type === FilterType.select)
        displayName =
            field.formatValue?.(value) ?? field.options?.find((option) => option.value === value)?.name ?? displayName
    if (field.type === FilterType.checkbox) displayName = value === 'true' ? 'Yes' : 'No'
    if (field.type === FilterType.datetime) {
        // Malformed bookmarks must remain harmless to pages that already ignore invalid date bounds.
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return undefined
        displayName =
            field.timezone === 'UTC'
                ? `${date.toISOString().replace('T', ' ').replace('.000Z', 'Z').replace('Z', ' UTC')}`
                : date.toLocaleString()
    }
    return {
        key: field.label,
        operator: field.operator ?? (field.type === FilterType.text ? 'matches' : 'is'),
        value,
        displayName,
    }
}

/** Declares each simple field once for tokens, URL restoration, defaults and column clicks. */
export function useListFilters({ fields, rawTextParam, pageParamName = 'page' }: ListFilterOptions) {
    const [params] = usePageParams<Record<string, string>>()
    const visibleFields = fields.filter((field) => !field.hidden)
    const templates = useMemo(() => fields.filter((field) => !field.hidden).map(fieldTemplate), [fields])
    const { appliedFilters, onFiltersApplied } = useUrlFilters<Record<string, string>>({
        pageParamName,
        mapUrlParamToFilterFn: (key, value) => {
            const field = visibleFields.find((field) => {
                const keys = [...(field.aliases ?? []), field.paramName]
                return (keys.find((key) => params[key]) ?? field.paramName) === key
            })
            return field ? toFilter(field, value) : undefined
        },
        mapFilterToUrlParamFn: (filter) => {
            const field = visibleFields.find((field) => field.label === filter.key)
            if (!field || !('value' in filter)) return {}
            return {
                ...Object.fromEntries((field.aliases ?? []).map((key) => [key, ''])),
                [field.paramName]:
                    field.type === FilterType.checkbox && filter.value === String(field.defaultValue ?? false)
                        ? ''
                        : filter.value,
            }
        },
    })
    const rawField = visibleFields.find((field) => field.paramName === rawTextParam)
    return {
        barProps: {
            appliedFilters,
            onFiltersApplied,
            searchTokenFilters: templates,
            rawTextFilter: rawField ? templates.find((template) => template.key === rawField.label) : undefined,
        },
        applyColumnFilter: (param: string, value: string) => {
            const field = visibleFields.find((field) => field.paramName === param || field.aliases?.includes(param))
            const incoming = field && toFilter(field, value)
            if (!incoming) return
            const merged = mergeFilter(appliedFilters, incoming)
            if (merged !== appliedFilters) onFiltersApplied(merged)
        },
    }
}
